import styles from "./Maps.module.scss";
import MapsBlockProps from "./MapsProps.interface";
import MapMarker from "./components/MapMarker/MapMarker";
import clustererMarkers from "./helpers/clustererMarkers";

import { GoogleMap, useJsApiLoader, MarkerClusterer } from "@react-google-maps/api";
import { useSelector } from "react-redux";
import combineClasses from "helpers/combine-classes";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import React, { FunctionComponent, useRef, useCallback } from "react";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import useAnchor from "blocks/hooks/useAnchor";
import { Fragment } from "react";
import isBrowserContext from "helpers/isBrowserContext";

const Maps: FunctionComponent<MapsBlockProps> = (props) => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const mapSize = props.settings.size ? styles[props.settings.size] : undefined;
    const settings = useSelector(state => state.settings.content);
    const mapStyles = settings?.theme?.googleMapStyles && JSON.parse(settings?.theme?.googleMapStyles);
    const clustererMarker = clustererMarkers(settings?.theme);
    const markers = props.content.markers;

    const identifier = useAnchor(props.settings.anchorMenuLabel);
    const { splitClasses, splitStyles } = useSplitBackground(props);

    const { isLoaded } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: "AIzaSyARZOcVyLEp-RkXlXed91gvaF3po4ArMu4",
    });
    const mapRef = useRef(null);

    const onLoad = useCallback(function callback(map: google.maps.Map) {
        if (isBrowserContext() && !props.settings?.zoomEnabled) {
            const bounds = new window.google.maps.LatLngBounds();

            markers.map(item => {
                const position = { lat: Number(item.content.latitude), lng: Number(item.content.longitude) };
                bounds.extend(position);
            });

            map.fitBounds(bounds, props.settings.mapPadding ? Number(props.settings.mapPadding) : 80);
        }
    }, []);

    const mapsOptions: google.maps.MapOptions = {
        styles: mapStyles,
        disableDefaultUI: true,
        zoomControl: true
    };

    const containerStyle = {
        height: props.settings.height ? `${props.settings.height}px` : "600px"
    };

    /*  With the callback above, zoom does not. There is made a boolean that disable the callback, if the zoom
        is enabled. As of now the props.settings.latitude and props.settings.longitude is a decimal data type. 
        And there is a issue with minus values. 
        There is added two more fields, with a Textstring datatype. 
        OBS. See Umbraco for more info. 
    */
   
    const center = {
        lat: props.settings.latitude ? props.settings.latitude : props.settings.mapLatitude ? parseFloat(props.settings.mapLatitude) : 56.2265413,
        lng: props.settings.longitude ? props.settings.longitude : props.settings.mapLongitude ? parseFloat(props.settings.mapLongitude) : 10.421698
    };

    const zoomLevel = props.settings.zoomLevel ? props.settings.zoomLevel : 8;

    return (
        <section className={combineClasses([styles.wrapper, ...wrapperClasses, ...splitClasses, mapSize])} style={{ ...wrapperStyles, ...splitStyles }} id={identifier}>
            <div className={combineClasses([styles.container, mapSize])}>

                {/* Loading */}
                {!isLoaded ? (
                    <div>Loading Maps</div>
                ) : null}

                {/* Markers */}
                {isLoaded === true ? (
                    <GoogleMap ref={mapRef} onLoad={onLoad} center={center} zoom={zoomLevel} options={mapsOptions} mapContainerStyle={containerStyle}>
                        {markers && markers.length > 0 ? (
                            <MarkerClusterer styles={clustererMarker} maxZoom={12} options={{ gridSize: 30, minimumClusterSize: 1 }}>
                                {(clusterer) =>
                                    <Fragment>
                                        {markers.map((marker, index) => {
                                            const markerWidth = marker?.content?.customMarker?.umbracoWidth ? marker.content.customMarker.umbracoWidth : settings?.theme?.assetMapsPin?.umbracoWidth ? settings?.theme?.assetMapsPin.umbracoWidth : 60;
                                            const markerHeight = marker?.content?.customMarker?.umbracoHeight ? marker.content.customMarker.umbracoHeight : settings?.theme?.assetMapsPin?.umbracoHeight ? settings?.theme?.assetMapsPin.umbracoHeight : 60;

                                            return (
                                                <MapMarker key={marker.contentUdi} index={index} marker={marker} markerWidth={markerWidth} markerHeight={markerHeight} showClusters={props.settings.clusters} clusterer={clusterer} markerImageFallback={settings?.theme?.assetMapsPin?._url ? settings.theme.assetMapsPin._url : ""} />
                                            );
                                        })}
                                    </Fragment>
                                }
                            </MarkerClusterer>
                        ) : null}
                    </GoogleMap>
                ) : null}
            </div>
        </section>
    );
};

export default Maps;
